import { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'services/Translation'
import InputText from 'components/common/inputs/Text'
import { error } from 'styles/Colors'
import Typography from 'components/common/Typography'
import styled from 'styled-components'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { CS, downloadPicture } from 'back-end-api'
import LoaderMini from 'components/common/loaders/mini'
import BODYPARTS_LABELS from 'components/common/widgets/Dummy/constants/bodyparts.labels'
import ImageModal from 'components/common/ImageModal'

import Appointment from './Appointment'

const Wrapper = styled.div`
  grid-area: text;
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(24rem + 200px);
`

const HeaderContainer = styled.div`
  flex-shrink: 0;
`

const SummaryContainer = styled(({ editing, ...props }) => <div {...props} />)`
  position: relative;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  transition: all 0.3s ease;
  border-radius: 8px;
  
  ${({ editing }) => editing && `
    border: 2px solid #EB7C0C;
    background-color: #EB7C0C08;
    box-shadow: 0 0 20px rgba(235, 124, 12, 0.3);
    margin: -2px;
  `}
`

const SaveButton = styled.button`
  position: absolute;
  bottom: -38px;
  right: 20px;
  background: #EB7C0C;
  color: white;
  border: 2px solid #EB7C0C;
  border-top: none;
  padding: 8px 16px;
  border-radius: 0 0 6px 6px;
  font-family: coresanscr65;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(235, 124, 12, 0.3);
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: #d16a0a;
    border-color: #d16a0a;
    box-shadow: 0 4px 12px rgba(235, 124, 12, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(235, 124, 12, 0.3);
  }
`

const FormContainer = styled.div`
  padding-left: 1.5rem;
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    title: getText('Topic', 'Patient_Details_Tabs_Summary_Form'),
    emergencyTitle: getText('Potential Emergency', 'Patient_Details_Tabs_Summary_Form'),
    topicLabel: getText('Initial Topic : ', 'Patient_Details_Tabs_Summary_Form'),
    requestLabel: getText('Patient Requests', 'Patient_Details_Tabs_Summary_Form'),
    editBtnOn: getText('Done', 'Patient_Details_Tabs_Summary_Form'),
    editBtnOff: getText('Edit', 'Patient_Details_Tabs_Summary_Form'),
    copyBtnOn: getText('Copied!', 'Patient_Details_Tabs_Summary_Form'),
    copyBtnOff: getText('Copy summary', 'Patient_Details_Tabs_Summary_Form'),
    rawInfo: getText('Raw information', 'Patient_Details_Tabs_Summary_Form'),
    oneSkipped: getText('skipped question', 'Patient_Details_Tabs_Summary_Form'),
    multiSkipped: getText('skipped questions', 'Patient_Details_Tabs_Summary_Form'),
    redFlags: getText('Red Flags', 'Patient_Details_Tabs_Summary_Form'),
    otherComplaints: getText('Other Complaints', 'Patient_Details_Tabs_Summary_Form'),
  }[key])

// Helper function to translate bodypart names in text based on language
const translateBodypartsInText = (text: string, targetLang: string): string => {
  if (!text || !targetLang) return text
  
  const labels = BODYPARTS_LABELS[targetLang as keyof typeof BODYPARTS_LABELS]
  const englishLabels = BODYPARTS_LABELS.en
  
  if (!labels || !englishLabels) return text
  
  let translatedText = text
  
  // Create a mapping from English labels to target language labels
  Object.keys(englishLabels).forEach(key => {
    const englishLabel = englishLabels[key as keyof typeof englishLabels]
    const targetLabel = labels[key as keyof typeof labels]
    
    if (englishLabel && targetLabel && englishLabel !== targetLabel) {
      // Use word boundary matching to avoid partial replacements
      // Case-insensitive replacement
      const regex = new RegExp(`\\b${englishLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi')
      translatedText = translatedText.replace(regex, targetLabel)
    }
  })
  
  return translatedText
}

const Summary = () => {
  const { t, lang } = useTranslation(texts)
  const { summary } = usePatientRouteParams()
  const [picture, setPicture] = useState<string | null>(null)
  const [loadingPicture, setLoadingPicture] = useState<boolean>(false)
  const [imageModalOpen, setImageModalOpen] = useState(false)

  useEffect(() => {
    const fetchAndDecodeImage = async () => {
      if (summary?.picture_url) {
        setLoadingPicture(true)
        const image = await downloadPicture(summary.picture_url)
        setPicture(image)
        setLoadingPicture(false)
      }
    }

    fetchAndDecodeImage()
  }, [summary?.picture_url])

  const rawSumup = CS.getSummarySumupTranslateIfNone(summary, lang) ?? ''
  // Translate bodypart names in the summary text based on current language
  const translatedSumup = translateBodypartsInText(rawSumup, lang)

  // Auto-translate complaint name if it's in a different language
  const [translatedComplaintName, setTranslatedComplaintName] = useState<string | null>(null)
  useEffect(() => {
    const complaintName = summary?.complaint?.name
    if (!complaintName) {
      setTranslatedComplaintName(null)
      return undefined
    }
    // Default to 'en' if no language_tag is set on the complaint
    const complaintLang = summary?.complaint?.language_tag || 'en'
    // If complaint is already in the user's language, no translation needed
    if (complaintLang === lang) {
      setTranslatedComplaintName(null)
      return undefined
    }
    let cancelled = false
    // @ts-ignore — translateText not yet in generated client
    CS.translateText({
      text: complaintName,
      from_language: complaintLang,
      to_language: lang,
    }).then((res) => {
      if (!cancelled && res.data?.translated_text) {
        setTranslatedComplaintName(res.data.translated_text)
      }
    }).catch(() => {
      // Translation failed, keep original
    })
    return () => { cancelled = true }
  }, [summary?.complaint?.name, summary?.complaint?.language_tag, lang])

  const [editing, setEditing] = useState(false)
  const [updatedSumup, setUpdatedSumup] = useState(translatedSumup)

  // Update the local state when the translated summary changes
  useEffect(() => {
    setUpdatedSumup(translatedSumup)
  }, [translatedSumup])

  const onEdit = useCallback(async () => {
    if (editing && updatedSumup !== summary?.sumup) {
      await CS.updateSummary({ id: summary!.id, sumup: { text: updatedSumup, language_tag: lang } })
    }
    setEditing(!editing)
  }, [editing, lang, summary, updatedSumup])

  const onSave = useCallback(async () => {
    if (updatedSumup !== summary?.sumup) {
      await CS.updateSummary({ id: summary!.id, sumup: { text: updatedSumup, language_tag: lang } })
    }
    setEditing(false)
  }, [lang, summary, updatedSumup])

  const updateSumup = useCallback(({ target: { value } }: any) => {
    setUpdatedSumup(value)
  }, [])

  if (!summary) return <LoaderMini center />

  return (
    <Wrapper>
      <HeaderContainer>
        <Appointment onEdit={onEdit} editing={editing} />
      </HeaderContainer>
      <SummaryContainer editing={editing}>
        <FormContainer>
          <div className="font-csc65 flex flex-col text-lg py-3 mb-3">
            {t('title')}
            <InputText
              className="mt-3 text-blue-500"
              field={{
                value: translatedComplaintName
                  ? `${translatedComplaintName} (${summary.complaint?.name})`
                  : summary.complaint?.name,
              }}
              noBorder
              noPadding
              containerStyle={{ flex: 1 }}
              readOnly
              inputStyle={undefined}
              style={undefined}
              label={undefined}
              inputRef={undefined}
              placeholderStyle={undefined}
            />
          </div>
          <div className="font-csc45 pb-3 mb-3 leading-7">
            <InputText
              field={{
                onChange: updateSumup,
                value: updatedSumup ?? '',
              }}
              inputStyle={{ lineHeight: '25px', fontSize: '15px' }}
              multiline
              noBorder
              noPadding
              readOnly={!editing}
              data-cy="sumupInput"
              style={undefined}
              label={undefined}
              inputRef={undefined}
              containerStyle={undefined}
              placeholderStyle={undefined}
            />
          </div>
          <div className="flex flex-col font-csc65 text-lg pb-3 mb-3">
            {t('redFlags')}
            <Typography
              className="mt-3 font-csc45"
              text={summary.red_flags?.join(', ')}
              color={error}
              size={15}
            />
          </div>
          {summary.patient_comment && (
            <div className="font-csc65 text-lg pb-3 mb-3">
              {t('otherComplaints')}
              <InputText
                className="font-csc45 text-xs pb-3 mb-3 leading-7"
                field={{
                  value: CS.getSummaryPatientCommentTranslateIfNone(summary, lang),
                }}
                noBorder
                noPadding
                inputStyle={{ lineHeight: '25px', fontSize: '15px' }}
                multiline
                readOnly
                rows={1}
                style={undefined}
                label={undefined}
                inputRef={undefined}
                containerStyle={undefined}
                placeholderStyle={undefined}
              />
            </div>
          )}
          {picture ? (
            <button
              type="button"
              onClick={() => setImageModalOpen(true)}
              aria-label="View full image"
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            >
              <img
                src={picture}
                alt=""
                width="300"
                height="400"
                style={{ borderRadius: '6px' }}
              />
            </button>
          ) : loadingPicture ? (
            <LoaderMini center />
          ) : null}
        </FormContainer>
      </SummaryContainer>
      {editing && (
        <SaveButton onClick={onSave}>
          Save
        </SaveButton>
      )}
      {imageModalOpen && picture && (
        <ImageModal src={picture} alt="Patient image" onClose={() => setImageModalOpen(false)} />
      )}
    </Wrapper>
  )
}

Summary.whyDidYouRender = true

export default memo(Summary)
